import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

const DISMISSED_KEY = "factcheckai_pwa_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari:
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // For iOS Safari, there is no beforeinstallprompt — show a manual hint after a delay
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => setShowIOSHint(true), 3500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setShowIOSHint(false);
  };

  const show = visible || showIOSHint;
  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        data-testid="install-prompt"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50"
      >
        <div className="border-2 border-black bg-white p-4 shadow-[6px_6px_0px_rgba(0,0,0,1)] flex items-start gap-3">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
            <DeviceMobile weight="fill" className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-1">
              ONE-TAP FROM ANY VIDEO APP
            </div>
            <div className="font-display text-lg text-zinc-950 leading-tight mb-1">
              Install TrueStream
            </div>
            <p className="text-[13px] text-zinc-700 leading-snug mb-3">
              {showIOSHint && !visible ? (
                <>Tap <b>Share</b> in Safari, then <b>&ldquo;Add to Home Screen&rdquo;</b>. TrueStream will appear in your share sheet from YouTube, TikTok, IG, X, Facebook.</>
              ) : (
                <>Add to your home screen so &ldquo;Share → TrueStream&rdquo; works from YouTube, TikTok, Instagram, X, and Facebook.</>
              )}
            </p>
            <div className="flex items-center gap-2">
              {deferredPrompt && (
                <button
                  onClick={install}
                  data-testid="install-app-button"
                  className="flex items-center gap-1.5 bg-black text-white px-3 py-1.5 font-mono-plex text-[10px] font-semibold hover:bg-zinc-800 transition-colors"
                >
                  <DownloadSimple weight="bold" className="w-3.5 h-3.5" />
                  INSTALL
                </button>
              )}
              <button
                onClick={dismiss}
                data-testid="dismiss-install-button"
                className="font-mono-plex text-[10px] font-semibold text-zinc-500 hover:text-zinc-900 px-2 py-1.5"
              >
                MAYBE LATER
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="flex-shrink-0 text-zinc-400 hover:text-zinc-900"
            aria-label="Dismiss"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
